import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, AlertTriangle, ChevronDown, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RuleFormDialog from "@/components/autoMessages/RuleFormDialog";
import { ANSWERED, CHANNELS, LOG_STATUS, SUGGESTED_TEMPLATES, describeRule } from "@/lib/autoMessagesConstants";
import { AUTO_MESSAGES } from "@/constants/testIds";

// Fix AN (2026-10-23): mensajes automáticos de WhatsApp (confirmación, recordatorio, seguimiento, recurrencia).
const SELECT_CLASS = "h-10 rounded-md border border-input bg-background px-3 text-sm";
const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;
const FROM_HOURS = Array.from({ length: 24 }, (_, i) => i);
const UNTIL_HOURS = Array.from({ length: 24 }, (_, i) => i + 1);
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export default function AutoMessagesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [services, setServices] = useState([]);
  const [log, setLog] = useState(null);
  const [logFilter, setLogFilter] = useState("");
  const [hours, setHours] = useState({ from: 9, until: 20 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const fail = (title, err) => toast({ title, description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/auto-messages");
      setData(d); setHours({ from: d.from_hour, until: d.until_hour }); setError("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron cargar los mensajes automáticos");
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data: d } = await api.get("/auto-messages/log", { params: logFilter ? { status: logFilter } : {} });
      setLog(d.items);
    } catch {
      setLog([]);
    }
  }, [logFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLog(); }, [loadLog]);
  useEffect(() => { api.get("/services").then(({ data: d }) => setServices(d)).catch(() => setServices([])); }, []);

  const serviceNames = (ids) => {
    if (!ids.length) return "Todos los tratamientos";
    return ids.map((id) => services.find((s) => s.id === id)?.name || "Tratamiento borrado").join(", ");
  };

  const putSettings = async (changes, okTitle) => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/auto-messages/settings", changes);
      setData((prev) => ({ ...prev, ...d })); setHours({ from: d.from_hour, until: d.until_hour });
      if (okTitle) toast({ title: okTitle });
    } catch (err) {
      fail("No se pudo guardar", err); load();
    } finally {
      setBusy(false);
    }
  };

  const saveRule = async (values) => {
    try {
      if (editing) await api.put(`/auto-messages/rules/${editing.id}`, values);
      else await api.post("/auto-messages/rules", values);
      toast({ title: editing ? "Regla guardada" : "Regla creada" });
      setFormOpen(false); setEditing(null); load();
      return true;
    } catch (err) {
      fail("No se pudo guardar", err);
      return false;
    }
  };

  const toggleRule = async (rule, active) => {
    try {
      await api.put(`/auto-messages/rules/${rule.id}/active`, { is_active: active });
      load();
    } catch (err) {
      fail("No se pudo cambiar la regla", err);
    }
  };

  const removeRule = async () => {
    const target = deleting; setDeleting(null);
    try {
      await api.delete(`/auto-messages/rules/${target.id}`);
      toast({ title: "Regla borrada" }); load();
    } catch (err) {
      fail("No se pudo borrar", err);
    }
  };

  const retry = async (item) => {
    try {
      const { data: d } = await api.post(`/auto-messages/log/${item.id}/retry`);
      toast(d.ok ? { title: "Mensaje enviado" } : { title: "Sigue sin poder enviarse", description: "Mirá el motivo en el registro.", variant: "destructive" });
    } catch (err) {
      fail("No se pudo reintentar", err);
    } finally {
      loadLog(); load();
    }
  };

  const hoursChanged = data && (hours.from !== data.from_hour || hours.until !== data.until_hour);
  const hoursInvalid = hours.from >= hours.until;

  return (
    <div className="max-w-4xl" data-testid={AUTO_MESSAGES.page}>
      <Link to="/conversaciones" className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-3 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Conversaciones</Link>
      <div className="mb-6">
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Mensajes automáticos</h1>
        <p className="text-base text-muted-foreground mt-1">Confirmaciones, recordatorios y seguimientos que se mandan solos por WhatsApp (con plantillas aprobadas) o por SMS.</p>
      </div>
      {error && <p className="text-sm text-destructive mb-4 break-words">{error}</p>}
      {data === null && !error && <p className="text-sm text-muted-foreground">Cargando...</p>}

      {data && (
        <>
          <div className="bg-white border border-border rounded-xl p-4 mb-4 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground">Mandar mensajes automáticos</p>
                <p className="text-sm text-muted-foreground">Interruptor general. Apagado, no se manda nada aunque haya reglas activas. Al prenderlo solo cuenta lo que pase de ahora en adelante.</p>
              </div>
              <Switch checked={data.enabled} disabled={busy} onCheckedChange={(v) => putSettings({ enabled: v }, v ? "Mensajes automáticos prendidos" : "Mensajes automáticos apagados")} data-testid={AUTO_MESSAGES.enabledToggle} />
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Se manda desde las</p>
                <select className={SELECT_CLASS} value={hours.from} onChange={(e) => setHours((h) => ({ ...h, from: Number(e.target.value) }))} data-testid={AUTO_MESSAGES.fromHourSelect}>
                  {FROM_HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">hasta las</p>
                <select className={SELECT_CLASS} value={hours.until} onChange={(e) => setHours((h) => ({ ...h, until: Number(e.target.value) }))} data-testid={AUTO_MESSAGES.untilHourSelect}>
                  {UNTIL_HOURS.map((h) => <option key={h} value={h}>{hourLabel(h === 24 ? 0 : h)}{h === 24 ? " (medianoche)" : ""}</option>)}
                </select>
              </div>
              <Button variant="outline" disabled={busy || !hoursChanged || hoursInvalid} onClick={() => putSettings({ from_hour: hours.from, until_hour: hours.until }, "Horario guardado")} data-testid={AUTO_MESSAGES.saveHoursButton}>Guardar horario</Button>
            </div>
            <p className="text-xs text-muted-foreground">Horario de la clínica. Fuera de ese horario el mensaje espera a que se abra; si ya no llega a tiempo (por ejemplo un recordatorio del mismo día), no se manda y queda anotado en el registro.</p>
          </div>

          {!data.templates_available && (
            <p className="text-sm text-[#B5793A] bg-[#F4E4D6] rounded-lg px-3 py-2 mb-4 flex items-start gap-2" data-testid={AUTO_MESSAGES.templatesWarning}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> No se pudieron leer las plantillas de WhatsApp en este momento. Mientras tanto no se manda ningún mensaje automático.
            </p>
          )}

          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <h2 className="font-serif text-2xl text-foreground">Reglas</h2>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-brand hover:bg-brand-hover text-white gap-1.5" data-testid={AUTO_MESSAGES.newRuleButton}>
              <Plus className="w-4 h-4" /> Nueva regla
            </Button>
          </div>
          {data.rules.length === 0 && <p className="text-sm text-muted-foreground mb-2" data-testid={AUTO_MESSAGES.rulesEmpty}>Todavía no hay reglas. Creá una con una plantilla aprobada.</p>}
          <div className="space-y-3 mb-6">
            {data.rules.map((r) => (
              <div key={r.id} className="bg-white border border-border rounded-xl p-4" data-testid={`${AUTO_MESSAGES.ruleRowPrefix}${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground break-words">{r.name} <Badge className="bg-muted text-muted-foreground border-none font-normal text-[11px] align-middle" data-testid={`${AUTO_MESSAGES.ruleChannelPrefix}${r.id}`}>{CHANNELS[r.channel] || "WhatsApp"}</Badge></p>
                    <p className="text-sm text-muted-foreground">{describeRule(r)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Switch checked={r.is_active} onCheckedChange={(v) => toggleRule(r, v)} aria-label={`Activar ${r.name}`} data-testid={`${AUTO_MESSAGES.ruleTogglePrefix}${r.id}`} />
                    <Button variant="ghost" size="sm" className="gap-1" onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`${AUTO_MESSAGES.ruleEditPrefix}${r.id}`}><Pencil className="w-3.5 h-3.5" /> Editar</Button>
                    <Button variant="ghost" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => setDeleting(r)} data-testid={`${AUTO_MESSAGES.ruleDeletePrefix}${r.id}`}><Trash2 className="w-3.5 h-3.5" /> Borrar</Button>
                  </div>
                </div>
                {r.channel === "sms" ? (
                  <p className="text-sm mt-2 break-words whitespace-pre-wrap" data-testid={`${AUTO_MESSAGES.ruleSmsTextPrefix}${r.id}`}>SMS: <span className="font-medium">{r.sms_text}</span></p>
                ) : (
                  <p className="text-sm mt-2 break-words">Plantilla: <span className="font-medium">{(r.template_name || "").replaceAll("_", " ")}</span> <span className="text-xs text-muted-foreground uppercase">{r.template_language}</span></p>
                )}
                <p className="text-sm text-muted-foreground mt-0.5 break-words">{serviceNames(r.service_ids)}</p>
                <p className="text-xs text-muted-foreground mt-1">{r.sent_count} {r.sent_count === 1 ? "enviado" : "enviados"}{r.last_sent_at ? ` · último ${fmtDateTime(r.last_sent_at)}` : ""}</p>
                {r.sms_status === "off" && (
                  <p className="text-xs text-[#B5793A] mt-2 flex items-start gap-1" data-testid={`${AUTO_MESSAGES.ruleSmsWarningPrefix}${r.id}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Los SMS no están activados: la regla no manda nada hasta que los actives en Agente IA &gt; SMS.</p>
                )}
                {r.template_status === "missing" && (
                  <p className="text-xs text-[#A4473C] mt-2 flex items-start gap-1" data-testid={`${AUTO_MESSAGES.ruleTemplateWarningPrefix}${r.id}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Esta plantilla ya no está aprobada o fue borrada: la regla no manda nada hasta que elijas otra.</p>
                )}
              </div>
            ))}
          </div>

          <Collapsible open={suggestionsOpen} onOpenChange={setSuggestionsOpen} className="mb-8">
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-2 text-sm font-medium text-foreground" data-testid={AUTO_MESSAGES.suggestionsToggle}>
                Textos sugeridos para crear en Plantillas <ChevronDown className={`w-4 h-4 transition-transform ${suggestionsOpen ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-3">
              <p className="text-sm text-muted-foreground">Crealas en <Link to="/conversaciones/plantillas" className="underline">Plantillas</Link> con la categoría Utilidad, el idioma que corresponda y los datos con nombre (nombre, tratamiento, fecha, hora e instrucciones). Sin tono de promoción, Meta las aprueba como Utilidad. Si una la clasifica como Marketing, no llega a números de Estados Unidos.</p>
              {SUGGESTED_TEMPLATES.map((s) => (
                <div key={s.key} className="bg-white border border-border rounded-xl p-4" data-testid={`${AUTO_MESSAGES.suggestionPrefix}${s.key}`}>
                  <p className="font-medium text-foreground">{s.title} <span className="text-xs text-muted-foreground font-normal">· {s.useFor}</span></p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-2 break-words">{s.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">Pie: {s.footer}</p>
                  {s.buttons.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{s.buttons.map((b) => <span key={b} className="text-xs border border-border rounded-full px-2.5 py-1">{b}</span>)}</div>}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>

          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <h2 className="font-serif text-2xl text-foreground">Registro</h2>
            <select className={SELECT_CLASS} value={logFilter} onChange={(e) => setLogFilter(e.target.value)} aria-label="Filtrar registro" data-testid={AUTO_MESSAGES.logFilterSelect}>
              <option value="">Todos</option>
              <option value="sent">Enviados</option>
              <option value="failed">Con error</option>
              <option value="skipped">No se mandaron</option>
            </select>
          </div>
          {log && log.length === 0 && <p className="text-sm text-muted-foreground" data-testid={AUTO_MESSAGES.logEmpty}>Todavía no se mandó ningún mensaje automático.</p>}
          <div className="space-y-2">
            {(log || []).map((l) => {
              const [label, color] = LOG_STATUS[l.status] || [l.status, "bg-muted text-muted-foreground"];
              return (
                <div key={l.id} className="bg-white border border-border rounded-xl px-4 py-3" data-testid={`${AUTO_MESSAGES.logRowPrefix}${l.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground break-words">{l.patient_name || "Sin nombre"} <span className="text-muted-foreground font-normal">· {l.rule_name}</span></p>
                      {l.subject_label && <p className="text-xs text-muted-foreground break-words">{l.subject_label}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {l.channel === "sms" && <Badge className="bg-muted text-muted-foreground border-none font-normal text-[11px]" data-testid={`${AUTO_MESSAGES.logChannelPrefix}${l.id}`}>SMS</Badge>}
                      {l.answered && ANSWERED[l.answered] && <Badge className="bg-[#E3EDE1] text-[#5B7A54] border-none font-normal text-[11px]">{ANSWERED[l.answered]}</Badge>}
                      <Badge className={`${color} border-none font-normal text-[11px]`}>{label}</Badge>
                      {l.status === "failed" && (
                        <Button variant="outline" size="sm" className="gap-1" onClick={() => retry(l)} data-testid={`${AUTO_MESSAGES.logRetryPrefix}${l.id}`}><RotateCcw className="w-3.5 h-3.5" /> Reintentar</Button>
                      )}
                    </div>
                  </div>
                  {l.reason && <p className="text-xs text-muted-foreground mt-1 break-words">{l.reason}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{fmtDateTime(l.sent_at || l.created_at)}</p>
                </div>
              );
            })}
          </div>
        </>
      )}

      <RuleFormDialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }} rule={editing} services={services} onSubmit={saveRule} smsAvailable={Boolean(data?.sms_available)} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar la regla {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Deja de mandar mensajes. El registro de lo que ya se mandó se conserva.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={removeRule} className="bg-destructive hover:bg-destructive/90" data-testid={AUTO_MESSAGES.ruleDeleteConfirm}>Borrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
